import {useEffect, useState} from "react";

import {usePublishAnalyticsEvent} from "@repo/analytics-learning-time";
import {useAssignedActivity} from "@repo/guide-core";
import {setPromptVariable} from "@repo/guide-core-helpers";
import {GuideStateApi, useGuideState} from "@repo/guide-state";
import type {Conversation} from "@repo/guide-types";
import {RequestInfo} from "@repo/shared-core";

function getHasStudentMessages(conversation: Conversation): boolean {
    return conversation.some((message) => message.user === "Student");
}

export const useAssignments = (message: string) => {
    const isMobile = RequestInfo.isMobile;

    // Assignable Activities controls
    const studentAssignmentData = useAssignedActivity();
    const [assignmentAttempted, setAssignmentAttempted] = useState(() => {
        const {conversation} = GuideStateApi.latest();
        return (
            !getHasStudentMessages(conversation) &&
            Boolean(studentAssignmentData)
        );
    });

    const [hasStudentMessages, setHasStudentMessages] = useState(() => {
        const {conversation} = GuideStateApi.latest();
        return getHasStudentMessages(conversation);
    });
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const threadId = useGuideState("threadId");

    // On desktop, both buttons are always shown. On mobile, only one button
    const showSubmitAssignmentButton =
        studentAssignmentData && (!isMobile || (isMobile && message === ""));
    useEffect(() => {
        if (studentAssignmentData) {
            setPromptVariable("isAssignment", "true");
        } else {
            setPromptVariable("isAssignment", "false");
        }
    }, [studentAssignmentData]);

    useEffect(() => {
        return GuideStateApi.on("changed", (fieldName) => {
            if (fieldName === "conversation") {
                const {conversation} = GuideStateApi.latest();
                if (
                    getHasStudentMessages(conversation) &&
                    !hasStudentMessages
                ) {
                    setHasStudentMessages(true);
                }
                if (
                    !getHasStudentMessages(conversation) &&
                    studentAssignmentData &&
                    !assignmentAttempted
                ) {
                    setAssignmentAttempted(true);
                }
            }
        });
    }, [hasStudentMessages, assignmentAttempted, studentAssignmentData]);

    const [currentThreadSubmitted, setCurrentThreadSubmitted] = useState(false);

    const submitButtonDisabled =
        message !== "" || currentThreadSubmitted || !hasStudentMessages;

    useEffect(() => {
        if (assignmentAttempted && threadId && studentAssignmentData) {
            publishAnalyticsEvent({
                eventType: "AiGuideActivityAssignmentStudentAttempted",
                eventSchemaVersion: 1,
                aiGuideThreadId: threadId,
                assignmentId: studentAssignmentData.id,
            });
        }
    }, [
        publishAnalyticsEvent,
        assignmentAttempted,
        threadId,
        studentAssignmentData,
    ]);

    const promptWhenNavigateAway =
        showSubmitAssignmentButton &&
        !submitButtonDisabled &&
        !currentThreadSubmitted;

    return {
        promptWhenNavigateAway,
        assignmentButtonProps: showSubmitAssignmentButton
            ? {
                  disabled: submitButtonDisabled,
                  studentAssignmentData,
                  currentThreadSubmitted,
                  setCurrentThreadSubmitted,
              }
            : null,
        currentThreadSubmitted,
        showSubmitAssignmentButton,
    };
};
